import { UserToggles } from '@ravago/shared/page-data/models/config';
import { ProductDetail } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { DEFAULT_PRODUCER_COLOR_CODE, OTHER_PRODUCER_COLOR_CODE, UNKNOWN_PRODUCER_COLOR_CODE } from '../../constants';
import { DynamicProductBlockContentGuard } from '../dynamic-product-block-content-guard';

import { RfqColorSelector, RfqColorSelectorValue } from './rfq-color-selector';
import { RfqLeadTime } from './rfq-lead-time';

interface Props {
   productDetail: ProductDetail | undefined;
   value: RfqColorSelectorValue;
   leadTimeIndicators: LeadTimeIndicator[];
   userToggles: UserToggles;
   leadTimeEnabled: boolean;
   onChange: (value: RfqColorSelectorValue) => void;
   hideExtraColorOptions?: boolean;
}

export const RequestForQuotation = ({
   value: colorSelectValue,
   leadTimeIndicators,
   productDetail,
   userToggles,
   leadTimeEnabled,
   onChange,
   hideExtraColorOptions = false
}: Props) => {
   const translate = useTranslations();

   const handleColorSelectChange = (newValue: RfqColorSelectorValue) => {
      onChange(newValue);
   };

   let { producerColorCode } = colorSelectValue;
   if (producerColorCode === OTHER_PRODUCER_COLOR_CODE) {
      producerColorCode = translate('color-codes.other');
   } else if (producerColorCode === UNKNOWN_PRODUCER_COLOR_CODE) {
      producerColorCode = translate('color-codes.unknown');
   }

   const formatProductDisplay = () => {
      if (!productDetail) return '';
      const selectedColor = productDetail?.colors?.find((color) => color.id === colorSelectValue.colorId);
      const colorName = selectedColor?.name;
      const displayParts = [productDetail.description];

      if (colorName && colorName.toLowerCase() !== UNKNOWN_PRODUCER_COLOR_CODE) {
         displayParts.push(colorName);
      }

      if (
         producerColorCode &&
         producerColorCode !== DEFAULT_PRODUCER_COLOR_CODE &&
         producerColorCode !== translate('color-codes.unknown') &&
         producerColorCode !== translate('color-codes.other')
      ) {
         displayParts.push(producerColorCode);
      }

      return displayParts.join(' – ');
   };

   return (
      <Box gap="md" direction="column">
         <DynamicProductBlockContentGuard productNotFound={!productDetail} userToggles={userToggles} leadTimeEnabled={leadTimeEnabled}>
            {productDetail?.colors && (
               <RfqColorSelector
                  id="dynamic-product-block-rfq-color-selector"
                  colors={productDetail.colors}
                  value={colorSelectValue}
                  onChange={handleColorSelectChange}
                  leadTimeIndicators={leadTimeIndicators}
                  leadTimeEnabled={leadTimeEnabled}
                  hideExtraColorOptions={hideExtraColorOptions}
               />
            )}
            <RfqLeadTime
               colorId={colorSelectValue.colorId}
               producerColorCode={colorSelectValue.producerColorCode}
               leadTimeIndicators={leadTimeIndicators}
               leadTimeEnabled={leadTimeEnabled}
            />
         </DynamicProductBlockContentGuard>
         {productDetail && (
            <>
               <Divider />
               <Box direction="column" gap="2xs">
                  <Typography variant="body-highlight">{translate('request-for-quotation.product')}</Typography>
                  <Typography>{formatProductDisplay()}</Typography>
               </Box>
            </>
         )}
      </Box>
   );
};
